/**
 * At Apartment procurement API.
 *
 * REACT_APP_PROCUREMENT_MOCK=true routes every call to the in-module fake
 * data below (same flag the centre procurement pages use), so the whole flow
 * — raise, approve, fulfil, receive — can be clicked through with no backend.
 */
import { fetchComplexes } from './apartment'
import kitsSeed from './apartmentKitsSeed.json'
import { apiEndpoints } from './common'

import { makeRequestUrl } from '../app/helpers'
import { get, patch, post } from '../request'

const USE_MOCK = process.env.REACT_APP_PROCUREMENT_MOCK === 'true'

/** Apartments as {uuid, name, cluster, status} for pickers and grids. */
export const fetchAptList = async () => {
  if (USE_MOCK) return MOCK_APARTMENTS
  const rows = await fetchComplexes()
  return (rows || []).map((r) => ({
    uuid: r.uuid,
    name: r.name,
    cluster: r.cluster_name || (r.cluster && r.cluster.name) || '',
    status: r.status || ''
  }))
}

// ---- real calls -----------------------------------------------------------

export const fetchMyAptRole = async () => {
  if (USE_MOCK) return mockRole()
  const response = await get(apiEndpoints.aptProcMyRole)
  return response.data
}

export const fetchAptKits = async (withItems) => {
  if (USE_MOCK) return mockKits(withItems)
  const url = makeRequestUrl(apiEndpoints.aptProcKits, {
    items: withItems ? '1' : undefined
  })
  const response = await get(url)
  return response.data
}

/** At Apartment catalogue additions (LXD-maintained, Sounak-approved). */
export const fetchAptCatalogue = async (queries = {}) => {
  if (USE_MOCK) return mockCatalogueList(queries)
  const url = makeRequestUrl(apiEndpoints.aptProcCatalogue, queries)
  const response = await get(url)
  return response.data
}

export const addAptCatalogueItem = async (data) => {
  if (USE_MOCK) return mockCatalogueAdd(data)
  const response = await post(apiEndpoints.aptProcCatalogue, data)
  return response.data
}

// Edit an existing catalogue item's fields (name / category / unit / link…).
export const editAptCatalogueItem = async (uuid, fields) => {
  if (USE_MOCK) return { uuid, ...fields }
  const response = await post(apiEndpoints.aptProcCatalogueAction, {
    uuid,
    action: 'edit',
    ...fields
  })
  return response.data
}

export const aptCatalogueAction = async (uuid, action, note) => {
  if (USE_MOCK) return mockCatalogueAction(uuid, action, note)
  const response = await post(apiEndpoints.aptProcCatalogueAction, {
    uuid,
    action,
    note
  })
  return response.data
}

// ---- categories (2026-09-17) ----------------------------------------------
// The six built-in budget heads, then the ones added from Alfred (e.g. Music).
export const fetchAptCategories = async () => {
  if (USE_MOCK) return APT_CATEGORIES.map((name) => ({ name, built_in: true }))
  const response = await get(apiEndpoints.aptProcCategories)
  return response.data
}

// Body {age_group: '3-5' | '5+', subject} -> the category, saved as "5+ Music".
export const addAptCategory = async (data) => {
  if (USE_MOCK) {
    return { name: `${data.age_group} ${data.subject}`, built_in: false }
  }
  const response = await post(apiEndpoints.aptProcCategories, data)
  return response.data
}

// ---- vendor master (HQ-controlled) ----------------------------------------
export const fetchAptVendors = async (queries = {}) => {
  if (USE_MOCK) return mockVendorList(queries)
  const url = makeRequestUrl(apiEndpoints.aptProcVendors, queries)
  const response = await get(url)
  return response.data
}

export const addAptVendor = async (data) => {
  if (USE_MOCK) return mockVendorAdd(data)
  const response = await post(apiEndpoints.aptProcVendors, data)
  return response.data
}

export const aptVendorAction = async (uuid, action, note) => {
  if (USE_MOCK) return mockVendorAction(uuid, action, note)
  const response = await post(apiEndpoints.aptProcVendorAction, {
    uuid,
    action,
    note
  })
  return response.data
}

/**
 * Search the MAIN program-material catalogue for a similar item, so an LXD
 * addition can reuse its reference id. Returns [{reference_id, name, category,
 * reference_link, unit}]. Reuses the centre procurement search endpoint.
 */
export const searchMainCatalogue = async (query = '') => {
  if (USE_MOCK) return mockMainCatalogueSearch(query)
  const url = makeRequestUrl(apiEndpoints.procurementCatalogue, {
    search: query,
    limit: 12
  })
  const response = await get(url)
  return (response.data && response.data.results) || []
}

export const fetchAptBudgets = async (queries = {}) => {
  if (USE_MOCK) return mockState.budgets
  const url = makeRequestUrl(apiEndpoints.aptProcBudget, queries)
  const response = await get(url)
  return response.data
}

export const saveAptBudget = async (data) => {
  if (USE_MOCK) return mockSaveBudget(data)
  const response = await post(apiEndpoints.aptProcBudget, data)
  return response.data
}

export const fetchAptOverview = async () => {
  if (USE_MOCK) return mockOverview()
  const response = await get(apiEndpoints.aptProcOverview)
  return response.data
}

export const fetchAptLifecycle = async () => {
  const response = await get(apiEndpoints.aptProcLifecycle)
  return response.data
}

export const fetchDailyImportTabs = async (url) => {
  const res = await get(
    makeRequestUrl(apiEndpoints.aptDailyImportTabs, { url })
  )
  return res.data
}

export const fetchDailyImportPreview = async (url, tab) => {
  const res = await get(
    makeRequestUrl(apiEndpoints.aptDailyImportPreview, { url, tab })
  )
  return res.data
}

export const commitDailyImport = async (url, tabs) => {
  const res = await post(apiEndpoints.aptDailyImportCommit, { url, tabs })
  return res.data
}

export const deleteAllDaily = async () => {
  const res = await post(apiEndpoints.aptDailyDeleteAll, {})
  return res.data
}

// The daily sheet write-back (2026-09-21): the Google sheet is still where
// the monthly report is made, so lines born in Alfred have to be pushed there.
export const fetchDailySheetTarget = async () => {
  const res = await get(apiEndpoints.aptDailyPushToSheet)
  return res.data
}

export const pushDailyToSheet = async (lines, day) => {
  const res = await post(apiEndpoints.aptDailyPushToSheet, { lines, day })
  return res.data
}

export const restoreDaily = async (uuids) => {
  const res = await post(apiEndpoints.aptDailyRestore, { uuids })
  return res.data
}

export const fetchAptRequests = async (queries = {}) => {
  if (USE_MOCK) return mockList(queries)
  const url = makeRequestUrl(apiEndpoints.aptProcRequests, queries)
  const response = await get(url)
  return response.data
}

export const fetchAptRequest = async (uuid) => {
  if (USE_MOCK) return mockGet(uuid)
  const response = await get(`${apiEndpoints.aptProcRequests}/${uuid}/`)
  return response.data
}

export const createAptRequest = async (data) => {
  if (USE_MOCK) return mockCreate(data)
  const response = await post(apiEndpoints.aptProcRequests, data)
  return response.data
}

export const updateAptRequest = async (uuid, data) => {
  if (USE_MOCK) return mockUpdate(uuid, data)
  const response = await patch(`${apiEndpoints.aptProcRequests}/${uuid}/`, data)
  return response.data
}

// `extra` carries the per-item actions' payload — `receive_at_hq` and
// `dispatch_from_at_apartment` take an optional `lines: [uuid]` to mark some of a
// request's lines rather than all of them (see _log_line_stage on the API).
export const aptRequestAction = async (uuid, action, reason, extra = {}) => {
  if (USE_MOCK) return mockAction(uuid, action, reason)
  const response = await post(apiEndpoints.aptProcRequestAction, {
    uuid,
    action,
    reason,
    ...extra
  })
  return response.data
}

export const generateAptKit = async (data) => {
  if (USE_MOCK) return mockGenerateKit(data)
  const response = await post(apiEndpoints.aptProcGenerateKit, data)
  return response.data
}

export const generateAptCategory = async (data) => {
  if (USE_MOCK) return mockGenerateCategory(data)
  const response = await post(apiEndpoints.aptProcGenerateCategory, data)
  return response.data
}

export const updateAptLine = async (data) => {
  if (USE_MOCK) return mockLine(data)
  const response = await post(apiEndpoints.aptProcLine, data)
  return response.data
}

export const deleteAptLine = async (uuid) => {
  if (USE_MOCK) return mockLine({ uuid, delete: true })
  const response = await post(apiEndpoints.aptProcLine, { uuid, delete: true })
  return response.data
}

// At Apartment's responses to an HQ-removed line: ask HQ why (it resurfaces on HQ's
// daily sheet flagged), or re-send it to be procured again (un-tombstones).
export const clarifyAptLine = async (uuid) => {
  if (USE_MOCK) return mockLine({ uuid, clarify: true })
  const response = await post(apiEndpoints.aptProcLine, { uuid, clarify: true })
  return response.data
}

export const resendAptLine = async (uuid) => {
  if (USE_MOCK) return mockLine({ uuid, resend: true })
  const response = await post(apiEndpoints.aptProcLine, { uuid, resend: true })
  return response.data
}

export const addAptLine = async (data) => {
  if (USE_MOCK) return mockAddLine(data)
  const response = await post(apiEndpoints.aptProcLineAdd, data)
  return response.data
}

// Persist a hand-sorted line order for one request (the grid's drag handle).
// Body: {request, lines: [uuid, ...]} top to bottom.
export const reorderAptLines = async (requestUuid, lineUuids) => {
  if (USE_MOCK) return { uuid: requestUuid, lines: [] }
  const response = await post(apiEndpoints.aptProcLineReorder, {
    request: requestUuid,
    lines: lineUuids
  })
  return response.data
}

// Bulk-add every approved At Apartment catalogue item in a category to the
// apartment's draft. Body: {apartment, category, sets?} — each line seeds at
// its required qty × sets (default 1).
export const bulkAddAptCategory = async (data) => {
  if (USE_MOCK) return { uuid: 'mock', lines: [] }
  const response = await post(apiEndpoints.aptProcBulkAddCategory, data)
  return response.data
}

// Per-apartment stock for one location (?apartment=uuid|hq|all): on-hand per
// item + the movement ledger + the location list for the picker.
export const fetchAptInventory = async (apartment = 'all') => {
  if (USE_MOCK)
    return { scope: apartment, locations: [], items: [], ledger: [] }
  const response = await get(
    `${apiEndpoints.aptProcInventory}?apartment=${encodeURIComponent(
      apartment
    )}`
  )
  return response.data
}

// Transfer stock between locations. Body: {item_key, qty, to_apartment,
// from_apartment?}. from defaults to the HQ store.
export const allocateAptInventory = async (data) => {
  if (USE_MOCK) return { ok: true }
  const response = await post(apiEndpoints.aptProcInventoryAllocate, data)
  return response.data
}

// Record stock used/consumed at a location. Body: {item_key, qty, apartment, note?}.
export const consumeAptInventory = async (data) => {
  if (USE_MOCK) return { ok: true }
  const response = await post(apiEndpoints.aptProcInventoryConsume, data)
  return response.data
}

// Record one physical stock check at a location. Body: {apartment, counts:
// [{item_key, item_name?, unit?, counted, reason?}], note?}. `counted` is what
// is actually on the shelf; the API writes ONE adjustment movement per item
// whose number moved and refuses the whole check if any of them moved without
// a reason. Items that agreed write nothing — the check row itself is what
// records that they were counted.
export const saveAptStockCheck = async (data) => {
  if (USE_MOCK) return { ok: true, counted: 0, differed: 0 }
  const response = await post(apiEndpoints.aptProcInventoryCount, data)
  return response.data
}

// Super-admin only: scrap/delete an item from inventory outright. Body: {item_key}.
export const deleteAptInventoryItem = async (item_key) => {
  if (USE_MOCK) return { ok: true }
  const response = await post(apiEndpoints.aptProcInventoryDeleteItem, {
    item_key
  })
  return response.data
}

// Record the manual approval sign-off on a daily-sheet request (who approved,
// the source and a free reference). Body: {uuid, approved_by_name?,
// approval_source?, approval_reference?}.
export const saveAptApproval = async (data) => {
  if (USE_MOCK) return data
  const response = await post(apiEndpoints.aptProcRequestApproval, data)
  return response.data
}

// How an approval reached HQ — mirrors the request sources plus an issue
// tracker, for the daily-sheet Approval popup.
export const APPROVAL_SOURCE_OPTIONS = [
  'Slack',
  'WhatsApp',
  'Email',
  'Verbal',
  'Issue tracker'
]

export const SOURCE_OPTIONS = [
  'Team to Fill',
  'Slack',
  'WhatsApp',
  'Email',
  'Verbal',
  'Launch kit'
]

export const APT_CATEGORIES = [
  '3-5 Language',
  '3-5 STEM',
  '3-5 Art',
  '5+ Public Speaking',
  '5+ Art',
  '5+ Robotics'
]

// ---- mock -----------------------------------------------------------------
// Five live apartments in two clusters + one opening next month, mirroring
// the real rollout (South 3, East 2, North opening Aug).

const A = (id, name, cluster, status, goLive) => ({
  uuid: id,
  name,
  cluster,
  status,
  go_live_date: goLive
})

const MOCK_APARTMENTS = [
  A('apt-1', 'Waterford', 'South', 'Active', '2026-05-15'),
  A('apt-2', 'Prestige Lakeside', 'South', 'Active', '2026-05-20'),
  A('apt-3', 'Sobha Dream Acres', 'South', 'Active', '2026-06-01'),
  A('apt-4', 'Brigade Meadows', 'East', 'Active', '2026-05-18'),
  A('apt-5', 'Purva Riviera', 'East', 'Active', '2026-06-10'),
  A('apt-6', 'Godrej Woodsman', 'North', 'Inactive', '2026-08-05')
]

export const fetchMockApartments = () => MOCK_APARTMENTS

const KIT_ITEMS_35 = [
  ['Animal cards', "What's that Sound?", '3-5 Language', 15, 'per_class'],
  ['Story dice (4.5 cm)', 'Story cube', '3-5 Language', 6, 'per_class'],
  ['Counting bears', 'Number play', '3-5 STEM', 24, 'per_class'],
  ['Pattern blocks', 'Number play', '3-5 STEM', 4, 'per_class'],
  ['Crayons (pack of 12)', 'Art basics', '3-5 Art', 1, 'per_child'],
  ['A4 drawing sheets', 'Art basics', '3-5 Art', 10, 'per_child']
]

// The REAL seed (302 items from the LXD inventory Excel) — same JSON the
// backend imports, copied to src/api/apartmentKitsSeed.json for the offline
// mock. Keep in sync with oh-api/apps/oh_procurement/data/apartment_kits.json.
const MOCK_KITS = kitsSeed.map((tpl) => ({
  uuid: `kit-${tpl.kind}`,
  kind: tpl.kind,
  name: tpl.name,
  active: true,
  item_count: tpl.items.length,
  items: tpl.items.map((it, i) => ({
    uuid: `${tpl.kind}-${i}`,
    segment: it.segment || '',
    item_name: it.item_name,
    reference_id: it.reference_id || '',
    category: it.category || '',
    qty_norm: it.qty_norm,
    qty_note: it.qty_note || '',
    norm_basis: it.norm_basis || 'fixed',
    capex_opex: it.capex_opex || '',
    reference_link: it.reference_link || '',
    file_ref: it.file_ref || '',
    sort_order: i
  }))
}))

let seq = 100
const nextId = (prefix) => `${prefix}-${++seq}`
let mockPoSeq = 0

const mkLine = (over) => ({
  uuid: nextId('line'),
  kit_item: null,
  segment: '',
  item_name: '',
  item_type: 'goods',
  category: '',
  centre_label: '',
  qty: 1,
  unit: '',
  capex_opex: '',
  print_specification: '',
  paper_kind: '',
  sides_per_file: null,
  total_sides: null,
  ppp: null,
  unit_cost: 0,
  line_total: 0,
  gst_rate: 18,
  gst_amount: 0,
  amount_inclusive: false,
  invoice_amount: 0,
  file_link: '',
  invoice_link: '',
  reference_id: '',
  source_of_request: '',
  vendor: '',
  reference_link: '',
  remarks: '',
  fulfilment_status: 'pending',
  expected_delivery: null,
  ordered_on: null,
  dispatched_on: null,
  delivered_on: null,
  fulfilment_note: '',
  receipt_status: 'pending',
  receipt_note: '',
  ...over
})

const mkRequest = (apt, over) => ({
  uuid: nextId('req'),
  apartment: apt.uuid,
  apartment_name: apt.name,
  cluster_name: apt.cluster,
  kit_template: null,
  kit_name: '',
  title: '',
  notes: '',
  status: 'draft',
  po_number: '',
  requested_by: 'me',
  requested_by_name: 'Aswathi (LXD)',
  submitted_at: null,
  cluster_approved_at: null,
  final_approved_at: null,
  rejected_at: null,
  rejection_stage: '',
  rejection_reason: '',
  handed_over_at: null,
  closed_at: null,
  created: new Date().toISOString(),
  lines: [],
  ...over
})

const seedRequests = () => {
  const launch = mkRequest(MOCK_APARTMENTS[0], {
    kit_template: 'kit-launch_3_5',
    kit_name: '3-5 Launch Kit',
    title: '3-5 Launch Kit — Waterford',
    status: 'handed_over',
    submitted_at: '2026-07-10T09:00:00Z',
    cluster_approved_at: '2026-07-10T14:00:00Z',
    final_approved_at: '2026-07-11T08:00:00Z',
    handed_over_at: '2026-07-15T10:00:00Z',
    lines: KIT_ITEMS_35.map(([item, segment, category, qty], i) =>
      mkLine({
        segment,
        item_name: item,
        centre_label: 'Corporate',
        category,
        qty: qty * (i % 2 ? 2 : 1),
        unit_cost: 40 + i * 12,
        line_total: qty * (i % 2 ? 2 : 1) * (40 + i * 12),
        vendor: i % 2 ? 'Anmol' : 'Amazon',
        fulfilment_status: i < 4 ? 'delivered' : 'dispatched',
        expected_delivery: i < 4 ? null : '2026-07-19',
        delivered_on: i < 4 ? '2026-07-15' : null
      })
    )
  })
  const pending = mkRequest(MOCK_APARTMENTS[3], {
    title: 'Speaker + mats top-up',
    status: 'submitted',
    submitted_at: '2026-07-16T11:00:00Z',
    lines: [
      mkLine({
        item_name: 'Bluetooth speaker',
        qty: 1,
        unit_cost: 2400,
        line_total: 2400
      }),
      mkLine({
        item_name: 'Foam mats - floor mats',
        qty: 2,
        unit_cost: 1500,
        line_total: 3000
      })
    ]
  })
  const forFinal = mkRequest(MOCK_APARTMENTS[4], {
    title: 'Robotics manuals reprint',
    status: 'cluster_approved',
    submitted_at: '2026-07-15T09:30:00Z',
    cluster_approved_at: '2026-07-16T12:00:00Z',
    lines: [
      mkLine({
        item_name: 'Manual - See Saw',
        category: '5+ Robotics',
        qty: 20,
        unit_cost: 55,
        line_total: 1100
      })
    ]
  })
  return [launch, pending, forFinal]
}

const mockState = {
  requests: seedRequests(),
  budgets: []
}

const seedBudgets = () => {
  const amounts = [40000, 30000, 25000, 30000, 25000, 60000]
  MOCK_APARTMENTS.slice(0, 5).forEach((apt) => {
    APT_CATEGORIES.forEach((category, i) => {
      mockState.budgets.push({
        uuid: nextId('bud'),
        apartment: apt.uuid,
        apartment_name: apt.name,
        cluster_name: apt.cluster,
        category,
        amount: amounts[i],
        notes: ''
      })
    })
  })
}
seedBudgets()

// Mirror the backend save() (2026-07-18): ppp is the single rate. Print lines
// price as total_sides x rate; goods as units x rate. GST is a rate %; when a
// line is GST-inclusive, the invoice is the input and taxable/GST derive from it.
const round2 = (n) => Math.round(Number(n || 0) * 100) / 100

const priceLine = (l) => {
  const qty = Number(l.qty || 0)
  const rate = Number(l.ppp || 0)
  const isPrint = (l.item_type || 'goods') === 'print'
  const sides = Number(l.sides_per_file || 0)
  l.total_sides = isPrint && sides ? sides * qty : null
  const base = isPrint ? Number(l.total_sides || 0) * rate : qty * rate
  const gstRate =
    l.gst_rate === '' || l.gst_rate == null ? 18 : Number(l.gst_rate)
  if (l.amount_inclusive) {
    const invoice = Number(l.invoice_amount || 0)
    const taxable = gstRate ? invoice / (1 + gstRate / 100) : invoice
    l.line_total = round2(taxable)
    l.gst_amount = round2(invoice - l.line_total)
  } else {
    l.line_total = round2(base)
    l.gst_amount = round2((base * gstRate) / 100)
    l.invoice_amount = round2(l.line_total + l.gst_amount)
  }
  return l
}

const summarise = (r) => ({
  ...r,
  lines: undefined,
  line_count: r.lines.length,
  total_value: r.lines.reduce((s, l) => s + Number(l.line_total || 0), 0),
  delivered_count: r.lines.filter((l) => l.fulfilment_status === 'delivered')
    .length
})

const mockRole = () => ({
  roles: [
    'LXD (Requester)',
    'Cluster Head',
    'Central Management',
    'Procurement HQ'
  ],
  clusters: [],
  apartments: [],
  can_request: true,
  is_central: true,
  is_hq: true
})

const mockKits = (withItems) =>
  withItems ? MOCK_KITS : MOCK_KITS.map(({ items, ...rest }) => rest)

// ---- catalogue additions (LXD adds -> Sounak approves) --------------------
// A flat list of items LXD added on top of the seeded kit catalogue. Two seeds
// so the approval flow is visible offline: one live, one awaiting Sounak.
const mockCatalogue = [
  {
    uuid: 'cat-1',
    item_name: 'Magnetic alphabet tiles (set of 100)',
    segment: 'Letter play',
    category: '3-5 Language',
    capex_opex: 'opex',
    unit: 'set',
    reference_id: 'PM_1042',
    reference_link: 'https://amazon.in/dp/example',
    matched_catalogue: 'main-1042',
    matched_catalogue_name: 'Magnetic Letters - 100 pc',
    status: 'approved',
    notes: '',
    added_by_name: 'Aswathi (LXD)',
    reviewed_by_name: 'Sounak',
    reviewed_at: '2026-07-16T10:00:00Z',
    review_note: ''
  },
  {
    uuid: 'cat-2',
    item_name: 'Robotics spare motor pack',
    segment: '',
    category: '5+ Robotics',
    capex_opex: 'capex',
    unit: 'pack',
    reference_id: '',
    reference_link: '',
    matched_catalogue: null,
    matched_catalogue_name: '',
    status: 'pending',
    notes: 'Frequent replacement item — keep 5 in stock.',
    added_by_name: 'Aswathi (LXD)',
    reviewed_by_name: '',
    reviewed_at: null,
    review_note: ''
  }
]

const mockCatalogueList = (queries = {}) => {
  let rows = [...mockCatalogue]
  if (queries.status) {
    const set = queries.status.split(',')
    rows = rows.filter((r) => set.includes(r.status))
  }
  return rows
}

const mockCatalogueAdd = (data) => {
  const row = {
    uuid: nextId('cat'),
    item_name: (data.item_name || '').trim(),
    segment: data.segment || '',
    category: data.category || '',
    capex_opex: data.capex_opex || '',
    unit: data.unit || '',
    reference_id: data.matched_reference_id || data.reference_id || '',
    reference_link: data.reference_link || '',
    matched_catalogue: data.matched_reference_id || null,
    matched_catalogue_name: data.matched_catalogue_name || '',
    status: 'pending',
    notes: data.notes || '',
    added_by_name: 'Aswathi (LXD)',
    reviewed_by_name: '',
    reviewed_at: null,
    review_note: ''
  }
  mockCatalogue.unshift(row)
  return row
}

let mockCatSeq = 900
const mockCatalogueAction = (uuid, action, note) => {
  const row = mockCatalogue.find((r) => r.uuid === uuid)
  if (!row) return null
  row.reviewed_by_name = 'Sounak'
  row.reviewed_at = new Date().toISOString()
  row.review_note = note || ''
  if (action === 'approve') {
    row.status = 'approved'
    if (!row.reference_id) row.reference_id = `AA_CT_${++mockCatSeq}`
  } else {
    row.status = 'rejected'
  }
  return row
}

// ---- vendor master mock ----------------------------------------------------
const mockVendors = [
  {
    uuid: 'ven-1',
    name: 'Anmol',
    gstin: '',
    contact: '',
    status: 'approved',
    notes: '',
    added_by_name: 'Procurement HQ',
    reviewed_by_name: 'Procurement HQ',
    reviewed_at: '2026-07-10T09:00:00Z',
    review_note: ''
  },
  {
    uuid: 'ven-2',
    name: 'Amazon',
    gstin: '',
    contact: '',
    status: 'approved',
    notes: '',
    added_by_name: 'Procurement HQ',
    reviewed_by_name: 'Procurement HQ',
    reviewed_at: '2026-07-10T09:00:00Z',
    review_note: ''
  },
  {
    uuid: 'ven-3',
    name: 'Vinayaka Stationers',
    gstin: '29ABCDE1234F1Z5',
    contact: '98450 00000',
    status: 'pending',
    notes: 'Proposed by LXD for craft supplies.',
    added_by_name: 'Aswathi (LXD)',
    reviewed_by_name: '',
    reviewed_at: null,
    review_note: ''
  }
]

const mockVendorList = (queries = {}) => {
  let rows = [...mockVendors]
  if (queries.status) {
    const set = queries.status.split(',')
    rows = rows.filter((v) => set.includes(v.status))
  }
  return rows
}

const mockVendorAdd = (data) => {
  const name = (data.name || '').trim()
  const existing = mockVendors.find(
    (v) => v.name.toLowerCase() === name.toLowerCase()
  )
  if (existing) return existing
  // Mock role is HQ, so a mock add lands approved (matches open-mode dev).
  const row = {
    uuid: nextId('ven'),
    name,
    gstin: data.gstin || '',
    contact: data.contact || '',
    status: 'approved',
    notes: data.notes || '',
    added_by_name: 'Procurement HQ',
    reviewed_by_name: 'Procurement HQ',
    reviewed_at: new Date().toISOString(),
    review_note: ''
  }
  mockVendors.unshift(row)
  return row
}

const mockVendorAction = (uuid, action, note) => {
  const row = mockVendors.find((v) => v.uuid === uuid)
  if (!row) return null
  row.status = action === 'approve' ? 'approved' : 'rejected'
  row.reviewed_by_name = 'Procurement HQ'
  row.reviewed_at = new Date().toISOString()
  row.review_note = note || ''
  return row
}

// Stand-in for the main program-material catalogue: search the seeded kit
// items by name, returning the reference id the LXD addition would reuse.
const mockMainCatalogueSearch = (query = '') => {
  const q = String(query).trim().toLowerCase()
  if (!q) return []
  const seen = new Set()
  const out = []
  for (const kit of MOCK_KITS) {
    for (const it of kit.items) {
      if (!it.reference_id || seen.has(it.reference_id)) continue
      if (`${it.item_name} ${it.reference_id}`.toLowerCase().includes(q)) {
        seen.add(it.reference_id)
        out.push({
          reference_id: it.reference_id,
          name: it.item_name,
          category: it.category,
          reference_link: it.reference_link,
          unit: ''
        })
        if (out.length >= 12) return out
      }
    }
  }
  return out
}

const mockSaveBudget = (data) => {
  const existing = mockState.budgets.find(
    (b) => b.apartment === data.apartment && b.category === data.category
  )
  if (existing) {
    existing.amount = Number(data.amount)
    existing.notes = data.notes || ''
    return existing
  }
  const apt = MOCK_APARTMENTS.find((a) => a.uuid === data.apartment) || {}
  const created = {
    uuid: nextId('bud'),
    apartment: data.apartment,
    apartment_name: apt.name || '',
    cluster_name: apt.cluster || '',
    category: data.category,
    amount: Number(data.amount),
    notes: data.notes || ''
  }
  mockState.budgets.push(created)
  return created
}

const SPEND = ['final_approved', 'in_daily', 'handed_over', 'closed']

const mockOverview = () =>
  MOCK_APARTMENTS.map((apt) => {
    const spent = {}
    let pipeline = 0
    mockState.requests
      .filter((r) => r.apartment === apt.uuid)
      .forEach((r) => {
        r.lines.forEach((l) => {
          const value = Number(l.line_total || 0)
          if (SPEND.includes(r.status)) {
            const key = l.category || ''
            spent[key] = (spent[key] || 0) + value
          } else if (['submitted', 'cluster_approved'].includes(r.status)) {
            pipeline += value
          }
        })
      })
    const categories = APT_CATEGORIES.map((category) => {
      const budget = mockState.budgets.find(
        (b) => b.apartment === apt.uuid && b.category === category
      )
      const used = spent[category] || 0
      if (!budget && !used) return null
      const amount = budget ? Number(budget.amount) : 0
      return { category, budget: amount, used, remaining: amount - used }
    }).filter(Boolean)
    return {
      apartment: apt.uuid,
      name: apt.name,
      cluster: apt.cluster,
      status: apt.status,
      go_live_date: apt.go_live_date,
      categories,
      uncategorised_spend: spent[''] || 0,
      pipeline_value: pipeline,
      kits: mockState.requests
        .filter(
          (r) =>
            r.apartment === apt.uuid &&
            r.kit_template &&
            SPEND.includes(r.status)
        )
        .map((r) => ({
          request: r.uuid,
          kit: r.kit_name,
          status: r.status,
          lines: r.lines.length,
          delivered: r.lines.filter((l) => l.fulfilment_status === 'delivered')
            .length,
          unavailable: r.lines.filter(
            (l) => l.fulfilment_status === 'unavailable'
          ).length
        }))
    }
  })

const mockList = (queries) => {
  let rows = [...mockState.requests]
  if (queries.apartment)
    rows = rows.filter((r) => r.apartment === queries.apartment)
  if (queries.status) {
    const statuses = queries.status.split(',')
    rows = rows.filter((r) => statuses.includes(r.status))
  }
  if (queries.lines === '1')
    return rows.map((r) => ({ ...summarise(r), lines: r.lines }))
  return rows.map(summarise)
}

const mockGet = (uuid) => mockState.requests.find((r) => r.uuid === uuid)

const mockCreate = (data) => {
  const apt = MOCK_APARTMENTS.find((a) => a.uuid === data.apartment)
  const req = mkRequest(apt, {
    title: data.title || '',
    notes: data.notes || '',
    lines: (data.lines || [])
      .filter((l) => (l.item_name || '').trim())
      .map((l) => priceLine(mkLine({ ...l })))
  })
  mockState.requests.unshift(req)
  return req
}

const mockUpdate = (uuid, data) => {
  const req = mockGet(uuid)
  if (!req) return null
  if ('title' in data) req.title = data.title
  if ('notes' in data) req.notes = data.notes
  if (req.status === 'rejected') req.status = 'draft'
  if ('lines' in data) {
    req.lines = (data.lines || [])
      .filter((l) => (l.item_name || '').trim())
      .map((l) => priceLine(mkLine({ ...l })))
  }
  return req
}

const mockAction = (uuid, action, reason) => {
  const req = mockGet(uuid)
  if (!req) return null
  const now = new Date().toISOString()
  if (action === 'submit') {
    req.status = 'submitted'
    req.submitted_at = now
    req.rejection_stage = ''
    req.rejection_reason = ''
  } else if (action === 'cluster_approve') {
    req.status = 'cluster_approved'
    req.cluster_approved_at = now
  } else if (action === 'final_approve') {
    // The head can final-approve straight from submitted (override cluster).
    if (req.status === 'submitted') req.cluster_approved_at = now
    req.status = 'final_approved'
    req.final_approved_at = now
  } else if (action === 'reject') {
    req.rejection_stage = req.status === 'submitted' ? 'cluster' : 'final'
    req.status = 'rejected'
    req.rejected_at = now
    req.rejection_reason = reason || ''
  } else if (action === 'add_to_daily') {
    req.status = 'in_daily'
    if (!req.po_number)
      req.po_number = `AA-PO-${String(++mockPoSeq).padStart(4, '0')}`
  } else if (action === 'hand_over') {
    req.status = 'handed_over'
    req.handed_over_at = now
  } else if (action === 'close') {
    req.status = 'closed'
    req.closed_at = now
  }
  return req
}

const mockGenerateKit = (data) => {
  const apt = MOCK_APARTMENTS.find((a) => a.uuid === data.apartment)
  const kit = MOCK_KITS.find((k) => k.kind === data.kind)
  const classes = Number(data.classes || 1)
  const children = Number(data.children || 1)
  const req = mkRequest(apt, {
    kit_template: kit.uuid,
    kit_name: kit.name,
    title: `${kit.name} — ${apt.name}`,
    lines: kit.items.map((item) => {
      const qty =
        item.norm_basis === 'per_class'
          ? item.qty_norm * classes
          : item.norm_basis === 'per_child'
          ? item.qty_norm * children
          : item.qty_norm
      return mkLine({
        kit_item: item.uuid,
        segment: item.segment,
        item_name: item.item_name,
        centre_label: 'Corporate',
        reference_id: item.reference_id || '',
        category: item.category,
        qty,
        capex_opex: item.capex_opex,
        reference_link: item.reference_link
      })
    })
  })
  mockState.requests.unshift(req)
  return req
}

// Every catalogue item of one category, across all kits, scaled like a kit.
const mockGenerateCategory = (data) => {
  const apt = MOCK_APARTMENTS.find((a) => a.uuid === data.apartment)
  const classes = Number(data.classes || 1)
  const children = Number(data.children || 1)
  const items = MOCK_KITS.flatMap((k) =>
    k.items.filter((it) => it.category === data.category)
  )
  const req = mkRequest(apt, {
    title: `${data.category} — ${apt.name}`,
    lines: items.map((item) => {
      const qty =
        item.norm_basis === 'per_class'
          ? item.qty_norm * classes
          : item.norm_basis === 'per_child'
          ? item.qty_norm * children
          : item.qty_norm
      return priceLine(
        mkLine({
          kit_item: item.uuid,
          segment: item.segment,
          item_name: item.item_name,
          centre_label: 'Corporate',
          reference_id: item.reference_id || '',
          category: item.category,
          qty,
          capex_opex: item.capex_opex,
          reference_link: item.reference_link
        })
      )
    })
  })
  mockState.requests.unshift(req)
  return req
}

const mockLine = (data) => {
  for (const req of mockState.requests) {
    const line = req.lines.find((l) => l.uuid === data.uuid)
    if (line) {
      if (data.delete) {
        req.lines = req.lines.filter((l) => l.uuid !== data.uuid)
        return req
      }
      Object.keys(data).forEach((key) => {
        if (key !== 'uuid' && key !== 'delete') line[key] = data[key]
      })
      priceLine(line)
      return req
    }
  }
  return null
}

// Sheet-style entry: append to the caller's open ad-hoc draft for the
// apartment, creating it if needed (mirrors the backend add-line endpoint).
const mockAddLine = (data) => {
  if (!data.apartment) {
    // HQ direct entry: no approval chain, born on the daily sheet.
    let direct = mockState.requests.find(
      (r) => !r.apartment && r.status === 'in_daily' && !r.kit_template
    )
    if (!direct) {
      direct = mkRequest(
        { uuid: '', name: '', cluster: '' },
        {
          title: 'Daily sheet — direct entries',
          status: 'in_daily',
          requested_by_name: 'Procurement HQ'
        }
      )
      direct.apartment = ''
      direct.apartment_name = ''
      direct.cluster_name = ''
      mockState.requests.unshift(direct)
    }
    direct.lines.push(priceLine(mkLine({ ...data })))
    return direct
  }
  const apt = MOCK_APARTMENTS.find((a) => a.uuid === data.apartment)
  let req = mockState.requests.find(
    (r) =>
      r.apartment === data.apartment && r.status === 'draft' && !r.kit_template
  )
  if (!req) {
    req = mkRequest(apt, { title: `Ad-hoc — ${apt.name}` })
    mockState.requests.unshift(req)
  }
  req.lines.push(priceLine(mkLine({ centre_label: 'Corporate', ...data })))
  return req
}
